// File: src/Components/payments/MasavMethodEditor.jsx
// ---------------------------------------------------------------------------
// Self-service editor letting an accountant/client save their MASAV (bank-debit)
// collection details, as an alternative to a credit card. Mirrors the admin
// MasavPaymentMethodEditor in the xtram manager dashboard.
// ---------------------------------------------------------------------------
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, ToggleButton, ToggleButtonGroup, TextField, Button,
  Chip, CircularProgress, Alert, Link,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from 'react-i18next';
import PaymentService from '../../Services/payment.service.js';

// Mirrors backend Enums.BillingPaymentMethodKind { CreditCard = 0, Masav = 1 }.
const KIND_CREDIT_CARD = 0;
const KIND_MASAV = 1;

const onlyDigits = (s) => (s || '').replace(/\D/g, '');

const MasavMethodEditor = ({ userId, userType, arrangement, onSaved }) => {
  const { t } = useTranslation();
  const [kind, setKind] = useState(KIND_CREDIT_CARD);
  const [bankCode, setBankCode] = useState('');
  const [branchNumber, setBranchNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [payerId, setPayerId] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [reference, setReference] = useState('');
  const [documentSignedUrl, setDocumentSignedUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [savedOk, setSavedOk] = useState(false);
  const fileInputRef = useRef(null);

  const get = (k) => arrangement?.[k] ?? arrangement?.[k.charAt(0).toUpperCase() + k.slice(1)];

  useEffect(() => {
    setKind(Number(get('paymentMethodKind') ?? KIND_CREDIT_CARD));
    setBankCode(get('masavBankCode') ?? '');
    setBranchNumber(get('masavBranchNumber') ?? '');
    setAccountNumber(get('masavAccountNumber') ?? '');
    setPayerId(get('masavPayerId') ?? '');
    setAccountHolderName(get('masavAccountHolderName') ?? '');
    setReference(get('masavReference') ?? '');
    setDocumentSignedUrl(get('masavDocumentSignedUrl') ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrangement]);

  const savedKind = Number(get('paymentMethodKind') ?? KIND_CREDIT_CARD);

  // Masav save is allowed only once the required bank fields are filled.
  const masavFilled = Boolean(bankCode && branchNumber && accountNumber);

  const handleSave = async () => {
    setError(null);
    setSavedOk(false);
    if (kind === KIND_MASAV && (!bankCode || !branchNumber || !accountNumber)) {
      setError(t('MasavBankFieldsRequired') || 'יש למלא קוד בנק, סניף ומספר חשבון');
      return;
    }
    setSaving(true);
    try {
      const svc = +userType === 2 ? PaymentService.accountant : PaymentService.client;
      await svc.upsertMasavMethod({
        UserId: userId,
        PaymentMethodKind: kind,
        MasavBankCode: kind === KIND_MASAV ? bankCode : '',
        MasavBranchNumber: kind === KIND_MASAV ? branchNumber : '',
        MasavAccountNumber: kind === KIND_MASAV ? accountNumber : '',
        MasavPayerId: kind === KIND_MASAV ? payerId : '',
        MasavAccountHolderName: kind === KIND_MASAV ? accountHolderName : '',
        MasavReference: kind === KIND_MASAV ? reference : '',
      });
      setSavedOk(true);
      if (onSaved) onSaved();
    } catch (e) {
      setError(e?.message || t('SaveFailed') || 'שמירה נכשלה');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const svc = +userType === 2 ? PaymentService.accountant : PaymentService.client;
      const res = await svc.uploadMasavDocument(userId, file);
      const url = res?.data?.url ?? res?.url;
      if (url) setDocumentSignedUrl(url);
      setSavedOk(true);
      if (onSaved) onSaved();
    } catch (err) {
      setError(err?.message || t('SaveFailed') || 'שמירה נכשלה');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AccountBalanceIcon sx={{ fontSize: 18 }} /> {t('MasavCollection') || 'גביית מס"ב (הוראת קבע)'}
        </Typography>
        {savedKind === KIND_MASAV
          ? <Chip size="small" color="info" icon={<AccountBalanceIcon />} label={t('Masav') || 'מס"ב'} />
          : <Chip size="small" color="success" icon={<CreditCardIcon />} label={t('CreditCard') || 'כרטיס אשראי'} />}
      </Box>

      <ToggleButtonGroup size="small" exclusive value={kind} onChange={(_, v) => { if (v !== null) setKind(v); }} sx={{ mb: 1.5 }}>
        <ToggleButton value={KIND_CREDIT_CARD} sx={{ textTransform: 'none' }}>
          <CreditCardIcon sx={{ fontSize: 16, mr: 0.5 }} /> {t('CreditCard') || 'כרטיס אשראי'}
        </ToggleButton>
        <ToggleButton value={KIND_MASAV} sx={{ textTransform: 'none' }}>
          <AccountBalanceIcon sx={{ fontSize: 16, mr: 0.5 }} /> {t('Masav') || 'מס"ב'}
        </ToggleButton>
      </ToggleButtonGroup>

      {kind === KIND_MASAV && (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
            <TextField size="small" label={t('BankCode') || 'קוד בנק'} value={bankCode} onChange={(e) => setBankCode(onlyDigits(e.target.value))} inputProps={{ maxLength: 8 }} sx={{ width: 110 }} />
            <TextField size="small" label={t('Branch') || 'סניף'} value={branchNumber} onChange={(e) => setBranchNumber(onlyDigits(e.target.value))} inputProps={{ maxLength: 8 }} sx={{ width: 110 }} />
            <TextField size="small" label={t('AccountNumber') || 'מספר חשבון'} value={accountNumber} onChange={(e) => setAccountNumber(onlyDigits(e.target.value))} inputProps={{ maxLength: 20 }} sx={{ width: 160 }} />
            <TextField size="small" label={t('PayerId') || 'ת"ז / מזהה משלם'} value={payerId} onChange={(e) => setPayerId(e.target.value)} inputProps={{ maxLength: 20 }} sx={{ width: 160 }} />
            <TextField size="small" label={t('AccountHolderName') || 'שם בעל החשבון'} value={accountHolderName} onChange={(e) => setAccountHolderName(e.target.value)} inputProps={{ maxLength: 64 }} sx={{ width: 220 }} />
            <TextField size="small" label={t('MasavReference') || 'אסמכתא מס"ב'} value={reference} onChange={(e) => setReference(e.target.value)} inputProps={{ maxLength: 64 }} sx={{ width: 220 }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
            <input ref={fileInputRef} type="file" hidden onChange={handleUploadDocument} accept=".pdf,.jpg,.jpeg,.png,.webp" />
            <Button
              variant="outlined"
              size="small"
              startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              sx={{ textTransform: 'none' }}
            >
              {t('UploadMasavDocument') || 'העלאת מסמך מס"ב'}
            </Button>
            {documentSignedUrl && (
              <Link href={documentSignedUrl} target="_blank" rel="noopener" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13 }}>
                <DescriptionIcon sx={{ fontSize: 16 }} /> {t('ViewMasavDocument') || 'צפייה במסמך'}
              </Link>
            )}
          </Box>
        </>
      )}

      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {savedOk && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSavedOk(false)}>{t('Saved') || 'נשמר'}</Alert>}
      {kind === KIND_MASAV && (
        <Box>
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={saving || !masavFilled}
            sx={{ textTransform: 'none' }}
          >
            {saving ? <CircularProgress size={18} /> : (t('SaveMasavDetails') || 'שמירת פרטי מס"ב')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

MasavMethodEditor.propTypes = {
  userId: PropTypes.string.isRequired,
  userType: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  arrangement: PropTypes.object,
  onSaved: PropTypes.func,
};

export default MasavMethodEditor;
